import { Box, LinearProgress, Paper, Stack, Tooltip, Typography } from '@mui/material';
import type { DocumentDashboardCard } from '../../../core/types/data-models';

/** Fixed row height used across every dashboard card grid (see CardGrid's `rowHeight` prop). */
export const DASHBOARD_CARD_HEIGHT = 220;
const DASHBOARD_CARD_DESCRIPTION_MAX_CHARS = 140;

/** How many grid columns a card should span by default, based on its content shape — TOP_LIST and TABLE cards need the extra width. Pass to CardGrid's `getColumnSpan` for a read-only grid; an editable grid (e.g. ProjectDetail's High Level Overview) instead uses a persisted, user-adjustable span. */
export function dashboardCardColumnSpan(card: DocumentDashboardCard): number {
  return card.cardType === 'TOP_LIST' || card.cardType === 'TABLE' ? 2 : 1;
}

/** Renders one DocumentDashboardCard according to its cardType. Fills its parent grid cell — sizing/spanning is the containing CardGrid's responsibility. */
export function DashboardCardTile({ card, sectionTitleById }: { card: DocumentDashboardCard; sectionTitleById: Map<number, string> }) {
  const numberValue = card.cardNumberValue != null ? Number(card.cardNumberValue) : null;
  const totalValue = card.cardNumberTotalValue != null ? Number(card.cardNumberTotalValue) : null;
  const progressPct = card.cardType === 'PROGRESS_PERCENT' && numberValue != null && totalValue
    ? Math.min(100, Math.max(0, Math.round((numberValue / totalValue) * 100)))
    : null;
  const tableRows = card.cardType === 'TABLE' && card.cardListValue
    ? card.cardListValue.map((row) => row.split('|').map((cell) => cell.trim()))
    : null;
  const isWide = dashboardCardColumnSpan(card) > 1;
  const description = card.cardDescription;
  const truncatedDescription = description && description.length > DASHBOARD_CARD_DESCRIPTION_MAX_CHARS
    ? `${description.slice(0, DASHBOARD_CARD_DESCRIPTION_MAX_CHARS).trimEnd()}…`
    : description;
  const sourceSectionTitles = card.sourceDocumentSectionIds
    .map((id) => sectionTitleById.get(id))
    .filter((title): title is string => !!title);

  return (
    <Paper
      variant="outlined"
      sx={{
        width: '100%',
        height: '100%',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 700, textAlign: 'center', textTransform: 'uppercase', letterSpacing: 0.3, flexShrink: 0 }}
      >
        {card.cardTitle}
      </Typography>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          mt: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          ...(isWide ? {} : { justifyContent: 'center', alignItems: 'center' }),
        }}
      >
        {card.cardType === 'NUMERIC' && (
          <Typography sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1.2, textAlign: 'center', color: 'var(--color-ink)' }}>
            {card.cardNumberValue}
            {card.cardNumberTotalValue && <> – {card.cardNumberTotalValue}</>}
          </Typography>
        )}

        {card.cardType === 'PROGRESS_PERCENT' && (
          <Box sx={{ width: '100%' }}>
            <Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2, textAlign: 'center', color: 'var(--color-ink)' }}>
              {card.cardNumberValue} / {card.cardNumberTotalValue}
            </Typography>
            {progressPct != null && (
              <LinearProgress
                variant="determinate"
                value={progressPct}
                sx={{ mt: 1, height: 6, borderRadius: 3 }}
              />
            )}
          </Box>
        )}

        {card.cardType === 'TOP_LIST' && card.cardListValue && (
          <Stack component="ol" spacing={0.5} sx={{ m: 0, pl: 2.5, width: '100%' }}>
            {card.cardListValue.map((item, i) => (
              <Typography key={i} component="li" variant="body2" sx={{ color: 'var(--color-ink-2)' }}>
                {item}
              </Typography>
            ))}
          </Stack>
        )}

        {card.cardType === 'TABLE' && tableRows && tableRows.length > 0 && (
          <Box sx={{ overflowX: 'auto', width: '100%' }}>
            <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
              <Box component="thead">
                <Box component="tr">
                  {tableRows[0].map((cell, i) => (
                    <Box
                      key={i}
                      component="th"
                      sx={{ textAlign: 'left', borderBottom: '1px solid', borderColor: 'divider', py: 0.5, pr: 1.5, fontWeight: 700 }}
                    >
                      {cell}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {tableRows.slice(1).map((row, i) => (
                  <Box component="tr" key={i}>
                    {row.map((cell, j) => (
                      <Box
                        key={j}
                        component="td"
                        sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 0.5, pr: 1.5, color: 'var(--color-ink-2)' }}
                      >
                        {cell}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {description && (
        <Tooltip title={description} arrow placement="top">
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, flexShrink: 0, cursor: 'help', textAlign: isWide ? 'left' : 'center' }}
          >
            {truncatedDescription}
          </Typography>
        </Tooltip>
      )}

      {sourceSectionTitles.length > 0 && (
        <Tooltip title={sourceSectionTitles.join(', ')} arrow placement="top">
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{
              mt: 0.25,
              flexShrink: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: isWide ? 'left' : 'center',
            }}
          >
            Source: {sourceSectionTitles.join(', ')}
          </Typography>
        </Tooltip>
      )}
    </Paper>
  );
}
